import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOnboardingStatus, dismissOnboarding } from '@/api/onboarding';
import type { StepStatus } from '@/api/onboarding';

export function useOnboarding() {
  return useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: getOnboardingStatus,
  });
}

/**
 * Pick the single next actionable onboarding step.
 *
 * The backend's per-step `recommended_next` is a fixed forward-pointer
 * (always "the next step in the sequence", i.e. order + 1) computed
 * independently of the caller's actual progress or blocking state -- it is
 * not a "do this now" signal. So we derive the true next action here from
 * `status` + `blocking_reason` + `order`: the lowest-order incomplete step
 * that isn't blocked, falling back to the first incomplete step if every
 * remaining step is blocked. Returns null once every step is completed.
 */
export function pickNextActionableStep(steps: StepStatus[]): StepStatus | null {
  const incomplete = [...steps]
    .filter((step) => step.status !== 'completed')
    .sort((a, b) => a.order - b.order);

  if (incomplete.length === 0) return null;

  return incomplete.find((step) => !step.blocking_reason) ?? incomplete[0];
}

export function useDismissOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissOnboarding,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
}
